// ONE function for "X signs on / off" (src/lib/rotationPeriods.js has the periods): the on-board toggle (crew cards + profile),
// the Crew Log entry, the Chat action and the AI tool sign_on_off all call applySignEvent(), so they do exactly the same:
//   - the Onboard flag, the Crew Log line, and the rotation:
//   sign OFF on D: the open Working period ends on D, a new Leave period starts D+1 with no end date (= ongoing);
//   sign ON  on D: the open Leave (or Working ashore ...) period ends D-1, a new Working period starts D with no end date.
// Only the CURRENT open period (no end date) is closed and only the new one is opened. Periods with an end date - anything
// edited by hand - are never touched. (Legacy: a signed-on person with no open Working period, last period ended before D ->
// the days since are saved as one Working period ending on D, so they are not lost.)
// The caller passes `io` (the database side: browser api.js or the AI's service-side db) and gets an `undo` record back.
import { leaveDays, nextDay, prevDay } from './rotationPeriods.js'

// rows: [{ id, type, start, end, days }] of ONE crew member (end '' / null = open)
export function planSignEvent(rows, { signingOn, date, onboard = false }) {
  const list = (rows || []).filter(r => r.start && (r.type || 'Leave') !== 'Paid Off').map(r => ({ ...r, type: r.type || 'Leave' }))
  const close = [], create = [], notes = []
  const openWork = list.filter(r => !r.end && r.type === 'Working')
  const openOther = list.filter(r => !r.end && r.type !== 'Working')
  const has = (type, start) => list.some(r => r.type === type && r.start === start)
  if (signingOn) {
    openOther.forEach(r => {
      if (String(r.start) < String(date)) { const end = prevDay(date); close.push({ id: r.id, end, days: leaveDays(r.start, end), prevEnd: null, prevDays: Number(r.days) || 0 }) }
      else notes.push(r.type + ' from ' + r.start + ' has no end date but starts on or after ' + date + ': left as it is')
    })
    if (!openWork.length && !has('Working', date)) create.push({ type: 'Working', start: date, end: null, days: 0 })
  } else {
    openWork.forEach(r => {
      if (String(r.start) <= String(date)) close.push({ id: r.id, end: date, days: leaveDays(r.start, date), prevEnd: null, prevDays: Number(r.days) || 0 })
      else notes.push('Working from ' + r.start + ' has no end date but starts after ' + date + ': left as it is')
    })
    if (!openWork.length && onboard) {
      const lastEnd = list.reduce((mx, r) => (r.end && r.end > mx ? r.end : mx), '')
      if (lastEnd && lastEnd < date && !openOther.length) create.push({ type: 'Working', start: nextDay(lastEnd), end: date, days: leaveDays(nextDay(lastEnd), date) })
    }
    const from = nextDay(date)
    if (!openOther.length && !has('Leave', from)) create.push({ type: 'Leave', start: from, end: null, days: 0 })
  }
  return { close, create, notes }
}

// io: { setOnboard(bool), addLog({ action, date, port, status, notes, entry }) -> id, patchPeriod(id, { end, days }),
//       addPeriod({ type, start, end, days, notes }) -> id, removePeriod(id), removeLog(id) }
// ev: { signingOn, date (YYYY-MM-DD), port, status, notes, name, onboard (before), rows, todayISO }
export async function applySignEvent(io, ev) {
  const plan = planSignEvent(ev.rows, ev)
  const undo = { closed: [], created: [], logId: null, prevOnboard: !!ev.onboard }
  const done = [], warn = [...plan.notes]
  await io.setOnboard(!!ev.signingOn)
  undo.logId = await io.addLog({ action: ev.signingOn ? 'Signed On' : 'Signed Off', date: ev.date, port: ev.port || '', status: ev.status || '', notes: ev.notes || '', entry: ev.entry || ((ev.name || '') + (ev.signingOn ? ' Signed On' : ' Signed Off') + (ev.port ? ' in ' + ev.port : '')), dateTo: ev.dateTo || null })
  for (const c of plan.close) {
    await io.patchPeriod(c.id, { end: c.end, days: c.days })
    undo.closed.push({ id: c.id, prevEnd: c.prevEnd, prevDays: c.prevDays })
    done.push('closed the open period on ' + c.end)
  }
  for (const n of plan.create) {
    let id
    try { id = await io.addPeriod({ ...n, notes: (ev.signingOn ? 'On rotation from ' : 'Off rotation from ') + (ev.port || ev.date) }) }
    catch (e) {
      // the database still wants an end date (sql/rotation-open-ended.sql not run): the old way, ending today (or the start)
      if (n.end) throw e
      const end = ev.todayISO && ev.todayISO >= n.start ? ev.todayISO : n.start
      id = await io.addPeriod({ ...n, end, days: leaveDays(n.start, end), notes: (ev.signingOn ? 'On rotation from ' : 'Off rotation from ') + (ev.port || ev.date) })
      warn.push('the database needs sql/rotation-open-ended.sql to keep a period open; ended it on ' + end)
    }
    if (id) undo.created.push(id)
    done.push('started ' + n.type + ' from ' + n.start + (n.end ? ' to ' + n.end : ' (ongoing)'))
  }
  return { done, warn, undo, plan }
}

export async function undoSignEvent(io, undo) {
  for (const id of undo.created || []) await io.removePeriod(id)
  for (const c of undo.closed || []) await io.patchPeriod(c.id, { end: c.prevEnd, days: c.prevDays })
  if (undo.logId) await io.removeLog(undo.logId)
  await io.setOnboard(!!undo.prevOnboard)
}
